import React, { useState, useEffect, useCallback } from 'react';
import { Award, Plus, Loader2, Edit3, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../AuthContext';
import configData from '../../configure'; // your config file with baseDomainResources
import './Grade.css';

const EVENT_START_DATE = '2025-10-05T00:00:00Z';
const EVENT_END_DATE = '2030-12-31T23:59:59Z';
const ACADEMIC_YEAR = '2025-2026';

// ────────────────────────────────────────────────
// Grading Templates
// ────────────────────────────────────────────────
const CBSE_GRADING_TEMPLATE = [
  { gradeName: 'A1', startPercentage: '91', endPercentage: '100', gradePoint: '10', gradeDescription: 'Outstanding' },
  { gradeName: 'A2', startPercentage: '81', endPercentage: '90', gradePoint: '9', gradeDescription: 'Excellent' },
  { gradeName: 'B1', startPercentage: '71', endPercentage: '80', gradePoint: '8', gradeDescription: 'Very Good' },
  { gradeName: 'B2', startPercentage: '61', endPercentage: '70', gradePoint: '7', gradeDescription: 'Good' },
  { gradeName: 'C1', startPercentage: '51', endPercentage: '60', gradePoint: '6', gradeDescription: 'Above Average' },
  { gradeName: 'C2', startPercentage: '41', endPercentage: '50', gradePoint: '5', gradeDescription: 'Average' },
  { gradeName: 'D', startPercentage: '33', endPercentage: '40', gradePoint: '4', gradeDescription: 'Fair' },
  { gradeName: 'E1', startPercentage: '21', endPercentage: '32', gradePoint: '', gradeDescription: 'Needs Improvement' },
  { gradeName: 'E2', startPercentage: '0', endPercentage: '20', gradePoint: '', gradeDescription: 'Unsatisfactory' },
];

const TELANGANA_STATE_GRADING_TEMPLATE = [
  { gradeName: 'A1', startPercentage: '91', endPercentage: '100', gradePoint: '10', gradeDescription: 'Outstanding' },
  { gradeName: 'A2', startPercentage: '81', endPercentage: '90', gradePoint: '9', gradeDescription: 'Excellent' },
  { gradeName: 'B1', startPercentage: '71', endPercentage: '80', gradePoint: '8', gradeDescription: 'Very Good' },
  { gradeName: 'B2', startPercentage: '61', endPercentage: '70', gradePoint: '7', gradeDescription: 'Good' },
  { gradeName: 'C', startPercentage: '51', endPercentage: '60', gradePoint: '6', gradeDescription: 'Above Average' },
  { gradeName: 'D', startPercentage: '41', endPercentage: '50', gradePoint: '5', gradeDescription: 'Average' },
  { gradeName: 'E', startPercentage: '35', endPercentage: '40', gradePoint: '4', gradeDescription: 'Fair' },
  { gradeName: 'F', startPercentage: '0', endPercentage: '34', gradePoint: '', gradeDescription: 'Failed' },
];

const Grade = ({ orgId }) => {
  const { axios, token, userRole } = useAuth();

  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [selectedExamName, setSelectedExamName] = useState('');
  const [gradeRules, setGradeRules] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gradeId, setGradeId] = useState(null);

  const [newGrades, setNewGrades] = useState([{
    gradeName: '',
    startPercentage: '',
    endPercentage: '',
    gradePoint: '',
    gradeDescription: '',
  }]);

  const [selectedTemplate, setSelectedTemplate] = useState('');

  // ────────────────────────────────────────────────
  // Fetch all exam events
  // ────────────────────────────────────────────────
  const fetchExams = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingExams(true);
      const response = await axios.get(configData.GET_EVENTS, {
        params: {
          orgId,
          eventStartDate: EVENT_START_DATE,
          eventEndDate: EVENT_END_DATE,
          pageSize: 200,
        },
      });

      const examEvents = (response.data?.items || [])
        .filter(event => {
          const name = (event.eventName || '').toLowerCase();
          const type = event.eventType || '';
          return type.includes('EXAM') || name.includes('exam') || name.includes('test') || name.includes('term');
        })
        .map(event => ({
          eventId: event.eventId,
          eventName: event.eventName?.trim() || 'Untitled Exam',
        }))
        .sort((a, b) => a.eventName.localeCompare(b.eventName));

      setExams(examEvents);

      if (examEvents.length > 0 && !selectedExamId) {
        setSelectedExamId(examEvents[0].eventId);
        setSelectedExamName(examEvents[0].eventName);
      }
    } catch (err) {
      console.error('Failed to load exams:', err);
      toast.error('Failed to load exams');
    } finally {
      setLoadingExams(false);
    }
  }, [token, axios, selectedExamId]);

  // ────────────────────────────────────────────────
  // Fetch grading system for selected exam
  // ────────────────────────────────────────────────
  const fetchGradingSystem = useCallback(async () => {
    if (!selectedExamId || !token) {
      setGradeRules([]);
      setGradeId(null);
      return;
    }

    setLoadingGrades(true);
    try {
      const url = `${configData.GET_GRADING_SYSTEM_BY_EVENT}/${selectedExamId}`;
      const response = await axios.get(url);

      const system = response.data?.[0];
      if (system?.gradeRules) {
        const sorted = system.gradeRules.sort((a, b) => b.startPercentage - a.startPercentage);
        setGradeRules(sorted);
        setGradeId(system.gradeId || null);
      } else {
        setGradeRules([]);
        setGradeId(null);
      }
    } catch (err) {
      console.error('Failed to load grading system:', err);
      toast.error('Failed to load grading system');
    } finally {
      setLoadingGrades(false);
    }
  }, [selectedExamId, token, axios]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  useEffect(() => {
    fetchGradingSystem();
  }, [fetchGradingSystem]);

  const handleOpenModal = () => {
    if (!selectedExamId) {
      toast.warning('Please select an exam first');
      return;
    }

    if (gradeRules.length > 0) {
      toast.info('A grading system already exists. Please use Edit button.');
      return;
    }

    setSelectedTemplate('');
    setNewGrades([{
      gradeName: '',
      startPercentage: '',
      endPercentage: '',
      gradePoint: '',
      gradeDescription: '',
    }]);
    setShowModal(true);
  };

  const handleTemplateChange = (e) => {
    const template = e.target.value;
    setSelectedTemplate(template);

    if (template === 'cbse') {
      setNewGrades(CBSE_GRADING_TEMPLATE.map(item => ({ ...item })));
      toast.info('CBSE grading template loaded');
    } else if (template === 'telangana') {
      setNewGrades(TELANGANA_STATE_GRADING_TEMPLATE.map(item => ({ ...item })));
      toast.info('Telangana State Board grading template loaded');
    } else {
      setNewGrades([{
        gradeName: '',
        startPercentage: '',
        endPercentage: '',
        gradePoint: '',
        gradeDescription: '',
      }]);
    }
  };

  const handleEditGradingSystem = () => {
    if (gradeRules.length === 0) {
      toast.warning('No grading system to edit');
      return;
    }

    setSelectedTemplate('');
    setNewGrades(gradeRules.map(grade => ({
      gradeName: grade.gradeName,
      startPercentage: grade.startPercentage.toString(),
      endPercentage: grade.endPercentage.toString(),
      gradePoint: grade.gradePoint?.toString() || '',
      gradeDescription: grade.gradeDescription || '',
    })));
    setShowModal(true);
  };

  const handleDeleteGradingSystem = async () => {
    if (!gradeId) return;

    if (!window.confirm('Are you sure you want to delete the entire grading system?')) return;

    setSaving(true);
    try {
      const url = `${configData.DELETE_GRADING_SYSTEM}/${gradeId}`;
      await axios.delete(url);
      toast.success('Grading system deleted successfully!');
      fetchGradingSystem();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete grading system');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate('');
  };

  const handleAddGradeRow = () => {
    setNewGrades([...newGrades, {
      gradeName: '',
      startPercentage: '',
      endPercentage: '',
      gradePoint: '',
      gradeDescription: '',
    }]);
  };

  const handleRemoveGradeRow = (index) => {
    if (newGrades.length === 1) {
      toast.warning('At least one grade is required');
      return;
    }
    setNewGrades(newGrades.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...newGrades];
    if (field === 'gradeName') {
      updated[index][field] = value.toUpperCase();
    } else {
      updated[index][field] = value;
    }
    setNewGrades(updated);
  };

  const validateGrades = () => {
    for (let i = 0; i < newGrades.length; i++) {
      const g = newGrades[i];
      const start = Number(g.startPercentage);
      const end = Number(g.endPercentage);

      if (!g.gradeName.trim() || !g.startPercentage || !g.endPercentage) {
        toast.error(`Grade ${i + 1}: Please fill all required fields (Name, Start %, End %)`);
        return false;
      }

      if (isNaN(start) || isNaN(end) || start < 0 || start > 100 || end < 0 || end > 100) {
        toast.error(`Grade ${i + 1}: Percentages must be between 0 and 100`);
        return false;
      }

      if (start > end) {
        toast.error(`Grade ${i + 1}: Start % must be less than or equal to End %`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedExamId) {
      toast.warning('Please select an exam');
      return;
    }

    if (!validateGrades()) return;

    setSaving(true);

    try {
      const gradeRulesPayload = newGrades.map((grade, index) => ({
        displayOrder: index + 1,
        startPercentage: Number(grade.startPercentage),
        endPercentage: Number(grade.endPercentage),
        gradeName: grade.gradeName.trim(),
        gradeDescription: grade.gradeDescription.trim() || null,
        gradePoint: grade.gradePoint ? Number(grade.gradePoint) : null,
      }));

      const basePayload = {
        academicYear: ACADEMIC_YEAR,
        description: "Grading system",
        eventId: selectedExamId,
        gradeRules: gradeRulesPayload,
        isActive: true,
        orgId,
      };

      if (gradeId) {
        // UPDATE
        const url = `${configData.UPDATE_GRADING_SYSTEM}/${gradeId}`;
        await axios.put(url, {
          ...basePayload,
          lastUpdatedAt: new Date().toISOString(),
        });
        toast.success('Grading system updated successfully!');
      } else {
        // CREATE
        await axios.post(configData.CREATE_GRADING_SYSTEM, {
          ...basePayload,
          createdAt: new Date().toISOString(),
        });
        toast.success('Grading system created successfully!');
      }

      handleCloseModal();
      fetchGradingSystem();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save grading system');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eg-grade-container">


      <div className="eg-grade-header">
        <h2 className="eg-grade-title">Marks Grade Management</h2>
        <div className="eg-header-actions">
          <div className="eg-exam-dropdown-wrapper">
            <select
              value={selectedExamId || ''}
              onChange={(e) => {
                const newId = e.target.value;
                setSelectedExamId(newId);
                const exam = exams.find(ex => String(ex.eventId) === String(newId));
                setSelectedExamName(exam?.eventName || '');
              }}
              className="eg-exam-dropdown"
              disabled={loadingExams}
            >
              {loadingExams ? (
                <option>Loading exams...</option>
              ) : (
                <>
                  <option value="">Select Exam</option>
                  {exams.map(exam => (
                    <option key={exam.eventId} value={exam.eventId}>
                      {exam.eventName}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {userRole === 'ADMIN' && (
            <>
              {gradeRules.length === 0 ? (
                <button
                  className="eg-add-grade-btn"
                  onClick={handleOpenModal}
                  disabled={loadingGrades || !selectedExamId}
                >
                  <Plus size={18} /> Add Grading System
                </button>
              ) : (
                <>
                  <button
                    className="eg-add-grade-btn eg-edit-grade-btn"
                    onClick={handleEditGradingSystem}
                    disabled={loadingGrades}
                  >
                    <Edit3 size={18} /> Edit
                  </button>

                  <button
                    className="eg-add-grade-btn eg-delete-grade-btn"
                    onClick={handleDeleteGradingSystem}
                    disabled={loadingGrades || saving}
                  >
                    <Trash2 size={18} /> Delete
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="eg-grade-card">
        <div className="eg-card-header">
          <h3 className="eg-card-title">Grade List</h3>
        </div>

        {loadingGrades ? (
          <div className="eg-loading-container">
            <Loader2 className="eg-loading-spinner" size={40} />
            <p className="eg-loading-text">Loading grading system...</p>
          </div>
        ) : gradeRules.length === 0 ? (
          <div className="eg-empty-state">
            <Award size={56} className="eg-empty-icon" />
            <p className="eg-empty-text">
              {!selectedExamId
                ? "Please select an exam above"
                : "No grading system defined for this exam yet"}
            </p>

            {selectedExamId && userRole === 'ADMIN' && (
              <button
                className="eg-add-grade-btn eg-big-add-button"
                onClick={handleOpenModal}
                style={{
                  marginTop: '24px',
                  padding: '12px 28px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                <Plus size={22} /> Create Grading System
              </button>
            )}
          </div>
        ) : (
          <div className="eg-table-wrapper">
            <table className="eg-grade-table">
              <thead className="eg-table-head">
                <tr>
                  <th className="eg-table-th">Grade</th>
                  <th className="eg-table-th">Percent Range</th>
                  <th className="eg-table-th eg-table-th-center">Grade Point</th>
                  <th className="eg-table-th">Description</th>
                </tr>
              </thead>
              <tbody className="eg-table-body">
                {gradeRules.map((rule, index) => (
                  <tr key={`${rule.gradeName}-${index}`} className="eg-table-row">
                    <td className="eg-table-td">
                      <span className={`eg-grade-badge eg-grade-${rule.gradeName.toLowerCase().replace('+', 'plus')}`}>
                        {rule.gradeName}
                      </span>
                    </td>
                    <td className="eg-table-td eg-percent-range">
                      {rule.startPercentage}% – {rule.endPercentage}%
                    </td>
                    <td className="eg-table-td eg-table-td-center eg-grade-point">
                      {rule.gradePoint ?? '—'}
                    </td>
                    <td className="eg-table-td eg-description">
                      {rule.gradeDescription || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="eg-modal-overlay" onClick={handleCloseModal}>
          <div className="eg-modal-content eg-modal-large" onClick={e => e.stopPropagation()}>
            <div className="eg-modal-header">
              <h3 className="eg-modal-title">
                {gradeId ? 'Edit Grading System' : 'Add Grading System'}
              </h3>
              <button className="eg-modal-close" onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            {!gradeId && (
              <div style={{
                padding: '16px 24px',
                background: '#f0f7ff',
                borderBottom: '1px solid #bfdbfe'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#1e40af'
                }}>
                  Quick Setup Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  className="eg-form-input"
                  style={{ width: '100%' }}
                >
                  <option value="">Select a template (optional)</option>
                  <option value="cbse">CBSE Standard Grading System</option>
                  <option value="telangana">Telangana State Board (SSC/Intermediate)</option>
                </select>
              </div>
            )}

            <form onSubmit={handleSubmit} className="eg-modal-form">
              <div className="eg-grades-list">
                {newGrades.map((grade, index) => (
                  <div key={index} className="eg-grade-form-card">
                    <div className="eg-grade-form-header">
                      <span className="eg-grade-form-number">Grade {index + 1}</span>
                      {newGrades.length > 1 && (
                        <button
                          type="button"
                          className="eg-remove-grade-btn"
                          onClick={() => handleRemoveGradeRow(index)}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    <div className="eg-form-group">
                      <label className="eg-form-label">Grade Name *</label>
                      <input
                        type="text"
                        value={grade.gradeName}
                        onChange={(e) => handleInputChange(index, 'gradeName', e.target.value)}
                        className="eg-form-input"
                        placeholder="e.g. A1, A2, B1"
                        maxLength={3}
                        required
                      />
                    </div>

                    <div className="eg-form-row">
                      <div className="eg-form-group">
                        <label className="eg-form-label">Start % *</label>
                        <input
                          type="number"
                          value={grade.startPercentage}
                          onChange={(e) => handleInputChange(index, 'startPercentage', e.target.value)}
                          className="eg-form-input"
                          min="0"
                          max="100"
                          required
                        />
                      </div>
                      <div className="eg-form-group">
                        <label className="eg-form-label">End % *</label>
                        <input
                          type="number"
                          value={grade.endPercentage}
                          onChange={(e) => handleInputChange(index, 'endPercentage', e.target.value)}
                          className="eg-form-input"
                          min="0"
                          max="100"
                          required
                        />
                      </div>
                      <div className="eg-form-group">
                        <label className="eg-form-label">Grade Point</label>
                        <input
                          type="number"
                          step="0.1"
                          value={grade.gradePoint}
                          onChange={(e) => handleInputChange(index, 'gradePoint', e.target.value)}
                          className="eg-form-input"
                        />
                      </div>
                    </div>

                    <div className="eg-form-group">
                      <label className="eg-form-label">Description</label>
                      <textarea
                        value={grade.gradeDescription}
                        onChange={(e) => handleInputChange(index, 'gradeDescription', e.target.value)}
                        className="eg-form-textarea"
                        rows="2"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" className="eg-add-another-btn" onClick={handleAddGradeRow}>
                <Plus size={18} /> Add Another Grade
              </button>

              <div className="eg-modal-footer">
                <button type="button" className="eg-btn-cancel" onClick={handleCloseModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="eg-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="eg-btn-spinner" size={16} /> Saving...
                    </>
                  ) : gradeId ? 'Update Grading System' : 'Save Grading System'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grade;